import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import CitaFormModal from "../components/CitaFormModal";
import { crearCitaVacia, crearFechaHora, crearFechaHoraActual, crearPayloadCita, obtenerPartesFechaHora } from "../../../dominio/cita";
import { useApi } from "../../../shared/api/api";
import AlertMessage from "../../../shared/components/AlertMessage";
import PaginationToolbar from "../../../shared/components/PaginationToolbar";
import { useAutoDismissMessage } from "../../../shared/hooks/useAutoDismissMessage";
import { formatCRC, formatDate, formatDateInputValue } from "../../../shared/utils/format";

const getRangoMesActual = () => {
  const hoy = new Date();
  const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const fin = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);

  return {
    inicio: formatDateInputValue(inicio),
    fin: formatDateInputValue(fin)
  };
};

export default function Citas() {
  const rangoInicial = getRangoMesActual();
  const [citas, setCitas] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [tiposCita, setTiposCita] = useState([]);
  const [form, setForm] = useState(crearCitaVacia);
  const [pacienteBusqueda, setPacienteBusqueda] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [cargandoCitas, setCargandoCitas] = useState(false);

  // Confirmaciones
  const [showConfirm, setShowConfirm] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [accionConfirmada, setAccionConfirmada] = useState(null);
  const [idAEliminar, setIdAEliminar] = useState(null);
  const [citaConfirmacion, setCitaConfirmacion] = useState(null);

  // Filtros
  const [fechaInicio, setFechaInicio] = useState(rangoInicial.inicio);
  const [fechaFin, setFechaFin] = useState(rangoInicial.fin);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [rangoConsulta, setRangoConsulta] = useState(rangoInicial);

  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const [registrosPorPagina, setRegistrosPorPagina] = useState(10);
  const cargaInicialEjecutada = useRef(false);
  const pacientesCargados = useRef(false);

  const { apiFetch } = useApi();
  useAutoDismissMessage(mensaje, () => setMensaje(null));

  const listarCitas = useCallback(async (rango = rangoConsulta) => {
    setCargandoCitas(true);

    try {
      const data = rango
        ? await apiFetch(`/citas.php?action=listByDate&inicio=${rango.inicio} 00:00:00&fin=${rango.fin} 23:59:59`)
        : await apiFetch("/citas.php?action=list");
      setCitas(data || []);
      setPaginaActual(1);
    } finally {
      setCargandoCitas(false);
    }
  }, [apiFetch, rangoConsulta]);

  const cargarPacientes = useCallback(async () => {
    if (pacientesCargados.current) return;
    pacientesCargados.current = true;
    const data = await apiFetch("/pacientes.php?action=list");
    setPacientes(data || []);
  }, [apiFetch]);

  const cargarTiposCita = useCallback(async () => {
    const data = await apiFetch("/tiposcita.php?action=list");
    setTiposCita(data);
  }, [apiFetch]);

  useEffect(() => {
    if (cargaInicialEjecutada.current) return;
    cargaInicialEjecutada.current = true;

    Promise.all([listarCitas(), cargarTiposCita()]);
  }, [listarCitas, cargarTiposCita]);

  // Obtener nombre de paciente
  const getNombrePaciente = (idPaciente) => {
    const paciente = pacientes.find((p) => String(p.IdPaciente) === String(idPaciente));
    return paciente ? paciente.Nombre : "N/A";
  };

  const getNombrePacienteEnLista = useCallback((idPaciente) => {
    const paciente = pacientes.find((p) => String(p.IdPaciente) === String(idPaciente));
    return paciente ? paciente.Nombre : "N/A";
  }, [pacientes]);

  const getEtiquetaPaciente = (paciente) => {
    const nombre = paciente?.Nombre || "";
    const telefono = paciente?.Telefono ? ` - ${paciente.Telefono}` : "";
    return `${nombre}${telefono}`;
  };

  const handleCostoChange = (e) => {
    const limpio = e.target.value.replace(/\D/g, "");
    setForm({ ...form, Costo: limpio });
  };

  const formatFechaCompleta = (fecha) => {
    if (!fecha) return "N/A";

    const fechaNormalizada = String(fecha).replace(" ", "T");
    const date = new Date(fechaNormalizada);

    if (Number.isNaN(date.getTime())) return fecha;

    return formatDate(date, { withTime: true });
  };

  const formatFechaSinHora = (fecha) => {
    if (!fecha) return "N/A";

    const fechaNormalizada = String(fecha).replace(" ", "T");
    const date = new Date(fechaNormalizada);

    if (Number.isNaN(date.getTime())) return fecha;

    return formatDate(date);
  };

  // Copiar descuento del tipo al seleccionar
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "IdTiposCita") {
      const tipo = tiposCita.find((t) => t.IdTiposCita.toString() === value);
      if (tipo) {
        setForm({
          ...form,
          IdTiposCita: value,
          Descuento: tipo.Descuento
        });
        return;
      }
    }

    // Check IVA (aplica 4% si está marcado, 0 si no)
    if (name === "IVA" && type === "checkbox") {
      setForm({ ...form, IVA: checked ? 4 : 0 });
      return;
    }

    setForm({ ...form, [name]: value });
  };

  const handlePacienteChange = (e) => {
    const value = e.target.value;
    const paciente = pacientes.find((p) => {
      const nombre = String(p.Nombre || "").toLowerCase();
      const etiqueta = getEtiquetaPaciente(p).toLowerCase();
      const busqueda = value.toLowerCase();
      return etiqueta === busqueda || nombre === busqueda;
    });

    setPacienteBusqueda(value);
    setForm({ ...form, IdPaciente: paciente ? paciente.IdPaciente : "" });
  };

  const handleFechaHoraChange = (name, value) => {
    const partesActuales = obtenerPartesFechaHora(form.Fecha);
    const nuevasPartes = { ...partesActuales, [name]: value };

    setForm({
      ...form,
      Fecha: crearFechaHora(nuevasPartes)
    });
  };

  // Modal antes de guardar (agregar/actualizar)
  const abrirNuevaCita = async () => {
    await cargarPacientes();
    setForm(crearCitaVacia({ Fecha: crearFechaHoraActual() }));
    setPacienteBusqueda("");
    setEditMode(false);
    setShowFormModal(true);
  };

  const abrirFiltro = () => {
    setShowFilterModal(true);
  };

  const cerrarFiltro = () => {
    setShowFilterModal(false);
  };

  const limpiarFiltro = () => {
    const rango = getRangoMesActual();
    setFechaInicio(rango.inicio);
    setFechaFin(rango.fin);
    setFiltroTipo("");
    setBusqueda("");
    setPaginaActual(1);
    setShowFilterModal(false);
    setRangoConsulta(rango);
    listarCitas(rango);
  };

  const cargarTodasLasCitas = () => {
    setFechaInicio("");
    setFechaFin("");
    setFiltroTipo("");
    setBusqueda("");
    setPaginaActual(1);
    setShowFilterModal(false);
    setRangoConsulta(null);
    listarCitas(null);
  };

  const aplicarFiltro = async (e) => {
    e.preventDefault();
    if (!fechaInicio || !fechaFin) {
      setMensaje({ tipo: "danger", texto: "Seleccione ambas fechas" });
      return;
    }
    
    const url = `listByDate&inicio=${fechaInicio} 00:00:00&fin=${fechaFin} 23:59:59`;
    const rango = { inicio: fechaInicio, fin: fechaFin };
    setCargandoCitas(true);

    try {
      const data = await apiFetch(`/citas.php?action=${url}`);
      setCitas(data || []);
      setPaginaActual(1);
      setShowFilterModal(false);
      setRangoConsulta(rango);
    } finally {
      setCargandoCitas(false);
    }
  };

  const handleBusquedaChange = (e) => {
    setBusqueda(e.target.value);
    setPaginaActual(1);
  };

  const guardarCita = (e) => {
    e.preventDefault();
    if (!form.IdPaciente) {
      setMensaje({ tipo: "danger", texto: "Seleccione un paciente valido de la lista" });
      return;
    }

    setCitaConfirmacion({
      PacienteNombre: pacienteBusqueda,
      Fecha: form.Fecha
    });
    setAccionConfirmada("guardar");
    setShowConfirm(true);
  };

  const confirmarGuardar = async () => {
    let resp;
    const payload = crearPayloadCita(form);

    if (editMode) {
      resp = await apiFetch("/citas.php?action=update", "POST", payload);
    } else {
      resp = await apiFetch("/citas.php?action=add", "POST", payload);
    }

    if (resp?.ok) {
      setMensaje({
        tipo: "success",
        texto: editMode ? "Cita actualizada" : "Cita agregada"
      });
      setForm(crearCitaVacia({ Fecha: crearFechaHoraActual() }));
      setPacienteBusqueda("");
      setEditMode(false);
      setShowFormModal(false);
      listarCitas();
    } else {
      setMensaje({ tipo: "danger", texto: resp?.error || "Ocurrio un error" });
    }

    setShowConfirm(false);
    setAccionConfirmada(null);
    setCitaConfirmacion(null);
  };

  const editarCita = async (cita) => {
    await cargarPacientes();
    // Aseguramos que IVA exista y sea número (0 ó 4)
    const iva = Number(cita.IVA) || 0;
    setForm({ ...cita, IVA: iva });
    setPacienteBusqueda(cita.PacienteNombre || getNombrePaciente(cita.IdPaciente));
    setEditMode(true);
    setShowFormModal(true);
  };

  // Modal antes de eliminar
  const eliminarCita = (cita) => {
    setIdAEliminar(cita.IdCita);
    setCitaConfirmacion(cita);
    setAccionConfirmada("eliminar");
    setShowConfirm(true);
  };

  const confirmarEliminar = async () => {
    const resp = await apiFetch("/citas.php?action=delete", "POST", { IdCita: idAEliminar });
    if (resp.ok) {
      setMensaje({ tipo: "success", texto: "Cita eliminada correctamente" });
      listarCitas();
    } else {
      setMensaje({ tipo: "danger", texto: resp.error || "Error al eliminar" });
    }
    setShowConfirm(false);
    setAccionConfirmada(null);
    setIdAEliminar(null);
    setCitaConfirmacion(null);
  };

  // Lista filtrada por Tipo de Cita y busqueda general (cliente)
  const citasFiltradas = useMemo(() => {
    const textoBusqueda = busqueda.trim().toLowerCase();

    return citas.filter((cita) => {
      const coincideTipo = !filtroTipo || String(cita.IdTiposCita) === String(filtroTipo);
      const nombrePaciente = String(cita.PacienteNombre || getNombrePacienteEnLista(cita.IdPaciente) || cita.IdPaciente || "");
      const fecha = formatFechaCompleta(cita.Fecha);
      const costo = formatCRC(cita.Costo);
      const ivaPct = String(cita.IVA || "");
      const textoCita = [
        nombrePaciente,
        cita.TipoCitaNombre,
        cita.IdTiposCita,
        fecha,
        costo,
        cita.Costo,
        cita.Descuento,
        `${cita.Descuento || 0}%`,
        ivaPct
      ].join(" ").toLowerCase();
      const coincideBusqueda = !textoBusqueda || textoCita.includes(textoBusqueda);

      return coincideTipo && coincideBusqueda;
    });
  }, [citas, filtroTipo, busqueda, getNombrePacienteEnLista]);

  // Calcular paginación
  const totalPaginas = Math.ceil(citasFiltradas.length / registrosPorPagina);
  const indiceInicio = (paginaActual - 1) * registrosPorPagina;
  const citasPaginadas = citasFiltradas.slice(indiceInicio, indiceInicio + registrosPorPagina);

  const cerrarConfirmacion = () => {
    setShowConfirm(false);
    setAccionConfirmada(null);
    setIdAEliminar(null);
    setCitaConfirmacion(null);
  };

  // Totales basados en la lista filtrada (incluye total IVA)
  const { totalCosto, totalAjuste, totalLibre, totalIVA } = useMemo(() => {
    let totalCosto = 0;
    let totalAjuste = 0;
    let totalLibre = 0;
    let totalIVA = 0;

    citasFiltradas.forEach((cita) => {
      const costo = Number(cita.Costo) || 0;
      const desc  = Math.min(Math.max(Number(cita.Descuento) || 0, 0), 100);
      const extra = Number(cita.MontoASumar) || 0; // viene del join con TiposCita
      const ivaPct = Math.min(Math.max(Number(cita.IVA) || 0, 0), 100);

      const ajuste = (costo * desc) / 100;
      const libre  = (costo - ajuste) + extra;
      const montoIVA = costo * (ivaPct / 100);

      totalCosto  += costo;
      totalAjuste += ajuste;
      totalLibre  += libre;
      totalIVA    += montoIVA;
    });

    return { totalCosto, totalAjuste, totalLibre, totalIVA };
  }, [citasFiltradas]);

  const nombreConfirmacion = citaConfirmacion?.PacienteNombre || pacienteBusqueda || "N/A";
  const fechaConfirmacion = formatFechaSinHora(citaConfirmacion?.Fecha || form.Fecha);

  return (
    <div className="container-fluid mt-3 px-0 px-md-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2 px-3 px-md-0">
        <div>
          <h2>Gestión de Citas</h2>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="btn btn-success btn-sm"  onClick={abrirFiltro}>
            Filtrar
          </button>
          <button type="button" className="btn btn-success btn-sm" onClick={abrirNuevaCita}>
            Agregar cita
          </button> 
        </div>
 
      </div>

      {/* Mensajes */}
      <AlertMessage message={mensaje} onClose={() => setMensaje(null)} />

      {/* Modal de filtros */}
      {showFilterModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Filtrar citas</h5>
                <button type="button" className="btn-close" onClick={cerrarFiltro}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={aplicarFiltro}>
                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <label className="form-label">Fecha inicio</label>
                      <input
                        type="date"
                        className="form-control"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Fecha fin</label>
                      <input
                        type="date"
                        className="form-control"
                        value={fechaFin}
                        onChange={(e) => setFechaFin(e.target.value)}
                        required
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Tipo de cita</label>
                      <select
                        className="form-select"
                        value={filtroTipo}
                        onChange={(e) => setFiltroTipo(e.target.value)}
                      >
                        <option value="">Todos</option>
                        {tiposCita.map((t) => (
                          <option key={t.IdTiposCita} value={t.IdTiposCita}>
                            {t.Nombre}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-12 d-flex justify-content-end gap-2">
                      <button type="button" className="btn btn-secondary btn-sm" onClick={cerrarFiltro}>Cerrar</button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={limpiarFiltro}>Mes actual</button>
                      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={cargarTodasLasCitas}>Todas</button>
                      <button type="submit" className="btn btn-primary btn-sm">Aplicar</button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      <CitaFormModal
        show={showFormModal}
        editMode={editMode}
        form={form}
        pacientes={pacientes}
        tiposCita={tiposCita}
        pacienteBusqueda={pacienteBusqueda}
        onPacienteChange={handlePacienteChange}
        onChange={handleChange}
        onFechaHoraChange={handleFechaHoraChange}
        onCostoChange={handleCostoChange}
        onSubmit={guardarCita}
        onClose={() => setShowFormModal(false)}
        getEtiquetaPaciente={getEtiquetaPaciente}
      />

      <div className="card mb-3">
        <div className="card-body admin-filter-bar">
          <input
            type="search"
            className="form-control"
            placeholder="Buscar por paciente, tipo, fecha, costo o descuento..."
            value={busqueda}
            onChange={handleBusquedaChange}
            aria-label="Buscar citas"
          />
        </div>
      </div>

      {/* Tabla */}
      <div className="card page-wide appointments-table-card">
        <div className="card-body">
          <h5 className="card-title">Listado de Citas</h5>
          <div className="table-responsive" style={{ overflowX: "auto" }}>
            <table className="table table-striped align-middle table-sm w-100 table-wide">
              <thead className="table-dark">
                <tr>
                  <th><span className="d-md-none">Paciente</span><span className="d-none d-md-inline">Paciente</span></th>
                  <th className="d-none d-md-table-cell">Fecha</th>
                  <th className="d-none d-md-table-cell">Tipo</th>
                  <th>Costo</th>
                  <th className="d-none d-md-table-cell">Desc.</th>
                  <th>%</th>
                  <th>Libre</th>
                  <th className="d-none d-lg-table-cell">IVA</th>
                  <th><span className="d-md-none">Acc.</span><span className="d-none d-md-inline">Acciones</span></th>
                </tr>
              </thead>
              <tbody>
                {cargandoCitas ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4">
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Cargando citas...
                    </td>
                  </tr>
                ) : citasPaginadas.length > 0 ? (
                  citasPaginadas.map((cita) => {
                    const costo = Number(cita.Costo) || 0;
                    const desc  = Math.min(Math.max(Number(cita.Descuento) || 0, 0), 100);
                    const extra = Number(cita.MontoASumar) || 0;
                    const ivaPct = Math.min(Math.max(Number(cita.IVA) || 0, 0), 100);

                    const montoAjuste = (costo * desc) / 100;
                    const montoLibre  = (costo - montoAjuste) + extra;
                    const montoIVA    = costo * (ivaPct / 100);

                    return (
                      <tr key={cita.IdCita}>
                        <td className="text-truncate" title={cita.PacienteNombre || cita.IdPaciente}>
                          <Link
                            className="patient-profile-link"
                            to={`/pacientes/${cita.IdPaciente}/perfil`}
                            state={{ from: "/citas", label: "citas" }}
                          >
                            {cita.PacienteNombre?.trim().split(" ")[0] || cita.IdPaciente}
                          </Link>
                        </td>
                        <td className="text-nowrap small text-capitalize d-none d-md-table-cell">
                          {formatFechaCompleta(cita.Fecha)}
                        </td>
                        <td className="text-truncate d-none d-md-table-cell" title={cita.TipoCitaNombre}>
                          {cita.TipoCitaNombre || cita.IdTiposCita}
                        </td>
                        <td>{formatCRC(costo)}</td>
                        <td className="d-none d-md-table-cell">{desc}%</td>
                        <td>{formatCRC(montoAjuste)}</td>
                        <td className="fw-bold">{formatCRC(montoLibre)}</td>
                        <td className="d-none d-lg-table-cell">{formatCRC(montoIVA)}</td>
                        <td>
                          <div className="btn-group btn-group-sm table-row-actions" role="group" aria-label="Editar o eliminar cita">
                            <button className="btn btn-primary" title="Editar" onClick={() => editarCita(cita)}>✎</button>
                            <button className="btn btn-danger" title="Eliminar" onClick={() => eliminarCita(cita)}>✕</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={9} className="text-center">{citasFiltradas.length === 0 ? "No hay citas" : "No hay citas en esta página"}</td></tr>
                )}
              </tbody>

              {/* Totales */}
              <tfoot>
                <tr className="table-secondary fw-bold d-md-none">
                  <td className="text-end">Totales:</td>
                  <td>{formatCRC(totalCosto)}</td>
                  <td>{formatCRC(totalAjuste)}</td>
                  <td>{formatCRC(totalLibre)}</td>
                  <td></td>
                </tr>
                <tr className="table-secondary fw-bold d-none d-md-table-row">
                  <td colSpan={3} className="text-end">Totales:</td>
                  <td>{formatCRC(totalCosto)}</td>
                  <td></td>
                  <td>{formatCRC(totalAjuste)}</td>
                  <td>{formatCRC(totalLibre)}</td>
                  <td className="d-none d-lg-table-cell">{formatCRC(totalIVA)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <PaginationToolbar
          page={paginaActual}
          totalPages={totalPaginas}
          pageSize={registrosPorPagina}
          disabled={cargandoCitas}
          onPageChange={setPaginaActual}
          onPageSizeChange={(size) => {
            setRegistrosPorPagina(size);
            setPaginaActual(1);
          }}
        />
      </div>

      {/* Modal de Confirmación */}
      {showConfirm && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar acción</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cerrarConfirmacion}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  {accionConfirmada === "eliminar"
                    ? "¿Estás seguro de que deseas eliminar la cita de "
                    : "¿Deseas guardar los cambios de la cita de "}
                  <strong>{nombreConfirmacion}</strong>
                  {" para el "}
                  <strong>{fechaConfirmacion}</strong>
                  ?
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cerrarConfirmacion}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className={`btn ${
                    accionConfirmada === "eliminar" ? "btn-danger" : "btn-success"
                  }`}
                  onClick={
                    accionConfirmada === "eliminar"
                      ? confirmarEliminar
                      : confirmarGuardar
                  }
                >
                  {accionConfirmada === "eliminar" ? "Eliminar" : "Guardar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

